import clsx from "clsx";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  formatDate,
  startOfMonth,
  subMonths,
} from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import { Button, Tooltip } from "devextreme-react";
import { Calendar, CalendarRef } from "devextreme-react/calendar";
import { LoadPanel } from "devextreme-react/load-panel";
import _ from "lodash";
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/auth";
import { useThemeContext } from "../../../theme/theme";
import { RoleEnum } from "../../../types";
import { useScreenSize } from "../../../utils/media-query";
import { getPresenceForUser } from "../api/Presence";
import { useViewSwitchContext } from "../contexts/ViewSwitchContext";
import { PresenceEntry } from "../types/Presence";
import { isHourlyAbsence } from "../utils/absenceHelpers";

type CellMeta = {
  bgColor: string;
  firstLine: string | null;
  secondLine: string | null;
  icon: JSX.Element | null;
  redBorder: boolean;
  violations: string[] | null;
  hourlyAbsenceLabel: string | null;
};

export const MyPresence = ({ embedded = false }: { embedded?: boolean }) => {
  const { t, i18n } = useTranslation();
  const authContext = useAuth();
  const themeContext = useThemeContext();
  const { setViewState } = useViewSwitchContext();
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;

  const calendarRef = useRef<CalendarRef>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState<Date>(() =>
    startOfMonth(new Date()),
  );
  const [presences, setPresences] = useState<PresenceEntry[]>([]);

  const presencesByDate = useMemo(() => {
    const map = new Map<string, PresenceEntry>();
    for (const p of presences) {
      map.set(p.date, p);
    }
    return map;
  }, [presences]);

  const fetchPresences = useCallback(
    async (year: number, month: number) => {
      try {
        setIsLoading(true);
        setPresences(await getPresenceForUser(year, month));
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : t("common.unknownErrorOccurred"),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    const isUserAuthorized =
      authContext.user!.roles!.some((role) =>
        [
          RoleEnum.Pracownik.toString(),
          RoleEnum.Majster.toString(),
          RoleEnum.Kierownik.toString(),
          RoleEnum.Manager.toString(),
        ].includes(role),
      ) || authContext.user!.isTimeManager;

    if (isUserAuthorized) {
      fetchPresences(currentDate.getFullYear(), currentDate.getMonth() + 1);
    } else if (!embedded) {
      setViewState("employees");
    }
  }, [fetchPresences, currentDate, authContext.user, setViewState, embedded]);

  const getCellMeta = useCallback(
    (date: Date): CellMeta => {
      const defaultBg =
        themeContext.theme === "dark" ? "bg-[#383838]" : "bg-[#e9e9e9]";
      const meta: CellMeta = {
        bgColor: defaultBg,
        firstLine: null,
        secondLine: null,
        icon: null,
        redBorder: false,
        violations: null,
        hourlyAbsenceLabel: null,
      };

      const entry = presencesByDate.get(format(date, "yyyy-MM-dd"));
      if (!entry) return meta;

      const hourly = isHourlyAbsence(entry.absence);
      if (hourly && entry.absence !== null) {
        meta.hourlyAbsenceLabel = `${entry.absence.acronym} ${entry.absence.hourFrom!.slice(
          0,
          5,
        )}-${entry.absence.hourTo!.slice(0, 5)}`;
      }

      if (entry.absence !== null && !hourly) {
        if (entry.absence.isLeave) {
          if (entry.absence.confirmed === 0 || entry.absence.confirmed === 1) {
            meta.bgColor = "bg-[#4a5565]";
          } else {
            meta.bgColor = "bg-[#1d4ed8]";
          }
        } else if (entry.absence.isSickLeave) {
          meta.bgColor = "bg-[#ad4ae2]";
        }

        meta.firstLine = entry.absence.acronym;

        if (entry.absence.synchronized !== null) {
          if (entry.absence.synchronized === true) {
            meta.icon = <i className="dx-icon-check text-green-500"></i>;
          } else if (entry.absence.confirmed === 2) {
            meta.icon = <i className="dx-icon-clock text-orange-400"></i>;
          } else {
            meta.icon = <i className="dx-icon-close text-red-500"></i>;
          }
        }
      } else if (entry.presence !== null) {
        if (entry.presence.confirmed === null) {
          meta.bgColor = "bg-emerald-400";
        } else if (entry.presence.confirmed === 0) {
          meta.bgColor = "bg-amber-700";
        } else if (entry.presence.confirmed === 1) {
          meta.bgColor = "bg-yellow-500";
        } else if (entry.presence.confirmed === 2) {
          meta.bgColor = "bg-emerald-600";
        }

        meta.firstLine = t("common.presenceAbbreviation", {
          value: entry.presence.label,
        });
        meta.secondLine = t("common.workPlanAbbreviation", {
          value: entry.workPlan?.label ?? t("common.none"),
        });

        if (entry.presence.synchronized !== null) {
          if (entry.presence.synchronized === true) {
            meta.icon = <i className="dx-icon-check text-green-500"></i>;
          } else if (entry.presence.confirmed === 2) {
            meta.icon = <i className="dx-icon-clock text-orange-400"></i>;
          } else {
            meta.icon = <i className="dx-icon-close text-red-500"></i>;
          }
        }
      } else {
        if (entry.workPlan?.dayType === 2) {
          meta.bgColor = "bg-[#4d7c0f]";
          if (entry.workPlan.dayOffType !== null) {
            meta.firstLine = entry.workPlan.dayOffType;
          }
        } else if (entry.workPlan?.dayType === 3) {
          meta.bgColor = "bg-[#a3e635]";
          if (entry.workPlan.dayOffType !== null) {
            meta.firstLine = entry.workPlan.dayOffType;
          }
        } else {
          meta.firstLine = t("common.workPlanAbbreviation", {
            value: entry.workPlan?.label ?? t("common.none"),
          });
        }
      }

      if (entry.violations.length > 0) {
        meta.redBorder = true;
        meta.violations = entry.violations;
      }

      return meta;
    },
    [presencesByDate, themeContext.theme, t],
  );

  const changeMonth = useCallback((direction: "next" | "previous") => {
    setCurrentDate((prev) =>
      startOfMonth(
        direction === "next" ? addMonths(prev, 1) : subMonths(prev, 1),
      ),
    );
  }, []);

  const goToToday = useCallback(() => {
    setCurrentDate(startOfMonth(new Date()));
  }, []);

  const monthDays = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate),
      }),
    [currentDate],
  );

  if (errorMessage) {
    return <div>{errorMessage}</div>;
  }

  if (isLoading) {
    return (
      <LoadPanel
        shading
        shadingColor="rgba(0, 0, 0, 0.3)"
        visible
        message={t("common.dataFetching")}
      />
    );
  }

  const legend = (
    <div
      className={clsx(
        "flex flex-wrap justify-center gap-4 md:gap-8 pt-4 text-sm mx-auto max-w-5xl",
        isMobile ? "text-xs" : "flex-col md:flex-row",
      )}
    >
      <div>
        <div className="font-semibold mb-2 text-gray-700 dark:text-gray-200">
          {t("presence.legend.workPlan")}
        </div>
        <div
          className={clsx(
            "gap-2",
            isMobile ? "flex flex-wrap" : "flex flex-col",
          )}
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#a3e635] rounded"></div>
            <div>{t("Holiday")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#4d7c0f] rounded"></div>
            <div>{t("Day off")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#1d4ed8] rounded"></div>
            <div>{t("Leave")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#ad4ae2] rounded"></div>
            <div>{t("holiday.HealthAbsence")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-[#4a5565] rounded"></div>
            <div>{t("Unconfirmed")}</div>
          </div>
        </div>
      </div>
      <div>
        <div className="font-semibold mb-2 text-gray-700 dark:text-gray-200">
          {t("presence.legend.presence")}
        </div>
        <div
          className={clsx(
            "gap-2",
            isMobile ? "flex flex-wrap" : "flex flex-col",
          )}
        >
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-700 rounded"></div>
            <div>{t("presence.unconfirmedPresence")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <div>{t("presence.confirmedByManager")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-600 rounded"></div>
            <div>{t("presence.legend.confirmed")}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-400 dark:bg-emerald-400 rounded"></div>
            <div>{t("presence.AddedInOptima")}</div>
          </div>
        </div>
      </div>
      <div>
        <div className="font-semibold mb-2 text-gray-700 dark:text-gray-200">
          {t("presence.legend.synchronization")}
        </div>
        <div
          className={clsx(
            "gap-2",
            isMobile ? "flex flex-wrap" : "flex flex-col",
          )}
        >
          <div className="flex items-center gap-2">
            <i className="dx-icon-close text-red-500"></i>
            <div>{t("Not synchronized")}</div>
          </div>
          <div className="flex items-center gap-2">
            <i className="dx-icon-clock text-orange-400"></i>
            <div>{t("Synchronizing")}</div>
          </div>
          <div className="flex items-center gap-2">
            <i className="dx-icon-check text-green-500"></i>
            <div>{t("Synchronized")}</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    const todayStr = format(new Date(), "yyyy-MM-dd");
    return (
      <>
        <div
          className={clsx(
            "sticky top-0 z-10 flex items-center gap-1 py-2",
            themeContext.theme === "dark" ? "bg-[#1e1e1e]" : "bg-white",
          )}
        >
          <Button
            icon="chevronprev"
            stylingMode="text"
            onClick={() => changeMonth("previous")}
          />
          <div className="flex-1 text-center text-base font-semibold">
            {`${_.upperFirst(
              format(currentDate, "LLLL", {
                locale: i18n.language === "en" ? enUS : pl,
              }),
            )} ${format(currentDate, "yyyy")}`}
          </div>
          <Button
            icon="chevronnext"
            stylingMode="text"
            onClick={() => changeMonth("next")}
          />
          <Button
            text={i18n.language === "en" ? "Today" : "Dziś"}
            stylingMode="outlined"
            onClick={goToToday}
          />
        </div>
        <div className="flex flex-col gap-2 pb-6">
          {monthDays.map((day) => {
            const meta = getCellMeta(day);
            const dayKey = format(day, "yyyy-MM-dd");
            const dayShort = _.upperFirst(
              format(day, "EEEEEE", {
                locale: i18n.language === "en" ? enUS : pl,
              }),
            );
            const isToday = dayKey === todayStr;
            return (
              <div
                key={dayKey}
                className={clsx(
                  "flex flex-col rounded-lg shadow-sm border-2 overflow-hidden",
                  meta.bgColor,
                  meta.redBorder ? "border-red-500" : "border-transparent",
                  isToday && !meta.redBorder ? "ring-2 ring-[#3c8dbc]" : "",
                )}
              >
                {meta.hourlyAbsenceLabel ? (
                  <div className="w-full px-2 py-1 bg-blue-700 text-white text-[11px] font-semibold text-center">
                    {meta.hourlyAbsenceLabel}
                  </div>
                ) : null}
                <div className="flex items-stretch gap-3 p-3">
                  <div className="flex flex-col items-center justify-center min-w-[48px] font-bold">
                    <div className="text-2xl leading-none">
                      {day.getDate()}
                    </div>
                    <div className="text-[11px] uppercase opacity-80 mt-1">
                      {dayShort}
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col justify-center text-sm min-w-0">
                    {meta.firstLine ? (
                      <div className="font-medium break-words">
                        {meta.firstLine}
                      </div>
                    ) : null}
                    {meta.secondLine ? (
                      <div className="text-xs opacity-90 break-words">
                        {meta.secondLine}
                      </div>
                    ) : null}
                    {meta.violations && meta.violations.length > 0 ? (
                      <ul className="mt-1 text-xs text-red-100 bg-red-600/80 rounded px-2 py-1 list-disc list-inside">
                        {meta.violations.map((v, i) => (
                          <li key={i}>{v}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  {meta.icon ? (
                    <div className="flex items-center text-xl">
                      {meta.icon}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        {legend}
      </>
    );
  }

  return (
    <>
      <Calendar
        className="mx-auto max-w-5xl"
        ref={calendarRef}
        firstDayOfWeek={1}
        width={"100%"}
        height={750}
        value={currentDate}
        onOptionChanged={(event) => {
          if (event.name === "currentDate") {
            setCurrentDate(event.value as Date);
          }
        }}
        cellRender={({
          date,
          text,
          view,
        }: {
          date: Date;
          text: string;
          view: string;
        }) => {
          if (view === "century" || view === "decade" || view === "year") {
            return text;
          }

          if (view === "month") {
            const meta = getCellMeta(date);

            return (
              <>
                <div
                  id={`cell-${formatDate(date, "yyyyMMdd")}`}
                  className={clsx(
                    `m-1 min-h-[100px] flex flex-col ${meta.bgColor} rounded-md font-bold border-solid border-3 overflow-hidden`,
                    {
                      "opacity-50": date.getMonth() !== currentDate.getMonth(),
                    },
                    { "border-red-500": meta.redBorder },
                    { "border-transparent": !meta.redBorder },
                  )}
                >
                  {meta.hourlyAbsenceLabel ? (
                    <div className="w-full px-1 py-0.5 bg-blue-700 text-white text-[10px] font-semibold text-center leading-tight">
                      {meta.hourlyAbsenceLabel}
                    </div>
                  ) : null}
                  <div className="flex-1 flex flex-col justify-center items-center gap-1">
                    <div className="mb-1">{text}</div>
                    {meta.firstLine ? <div>{meta.firstLine}</div> : null}
                    {meta.secondLine ? <div>{meta.secondLine}</div> : null}
                    {meta.icon}
                  </div>
                </div>
                {meta.violations ? (
                  <Tooltip
                    target={`#cell-${formatDate(date, "yyyyMMdd")}`}
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    hideOnOutsideClick={false}
                    position="bottom"
                  >
                    {meta.violations.map((v, i) => (
                      <div key={i}>{v}</div>
                    ))}
                  </Tooltip>
                ) : null}
              </>
            );
          }
        }}
      />
      {legend}
    </>
  );
};
